import React, { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { IoArrowForward, IoSearch } from 'react-icons/io5';

import {
    Box,
    Button,
    Flex,
    Group,
    Modal,
    Select,
    SimpleGrid,
    Stack,
    Text,
    TextInput,
    Title,
    UnstyledButton,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useIsMobile } from 'src/frontend/hooks/use-is-mobile';

import { all, Component, ComponentSection, ComponentType } from 'models';
import { CollectionHelpers } from 'helpers/CollectionHelpers';
import { ComponentHelpers } from 'helpers/ComponentHelpers';

import { ComponentDatasheetSectionProgress } from 'components/component-datasheet/components/ComponentDatasheetSectionProgress';
import { ComponentOverviewHit } from 'components/component-overview/ComponentOverviewHit';
import { DatasheetMantineProviderReset } from 'components/datasheet/DatasheetMantineProviderReset';
import { DatasheetMode, useDatasheetMode } from 'components/datasheet/DatasheetMode';
import { CompatibleWithField } from 'components/forms/fields/compatible-with/CompatibleWithField';
import { CompatiblePlaceholder } from 'components/forms/fields/compatible-with/components/CompatibleWithPlaceholders';
import { CarouselSection } from 'components/section/CarouselSection';
import { GridSection } from 'components/section/GridSection';
import { ComponentThumbnail } from 'components/thumbnail/ComponentThumbnail';
import { useCompanyProfiles } from 'hooks/use-company-profiles';
import { useProductsByIds } from 'hooks/use-products-by-ids';

import { ComponentDatasheetSection } from './ComponentDatasheetSection';

import cx from './CompatibleWith.module.scss';

const MODAL_THRESHOLD = 12;
const PREVIEW_COUNT = 3;

type CompatibleGroup = { type: ComponentType; items: Component[]; label: string };

const hasImage = (component: Component) => !!component.images?.some((image) => image.file);

const CompatibleProducts = ({ components }: { components: Component[] }) => {
    const groups = useMemo<CompatibleGroup[]>(() => {
        const byType = new Map<ComponentType, Component[]>();

        components.forEach((component) => {
            const items = byType.get(component.type) ?? [];
            items.push(component);
            byType.set(component.type, items);
        });

        return [...byType.entries()]
            .map(([type, items]) => ({ type, items, label: all[type]?.plural ?? type }))
            .sort((a, b) => b.items.length - a.items.length);
    }, [components]);

    const [modalType, setModalType] = useState<ComponentType | undefined>();

    if (components.length <= MODAL_THRESHOLD) {
        return (
            <CarouselSection nbCols={4}>
                {components.map((component) => (
                    <ComponentOverviewHit key={component.id} component={component} />
                ))}
            </CarouselSection>
        );
    }

    const modalGroup = groups.find((group) => group.type === modalType);

    return (
        <>
            <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }} spacing="sm">
                {groups.map((group) => (
                    <CompatibleTypeTeaser key={group.type} group={group} onOpen={() => setModalType(group.type)} />
                ))}
            </SimpleGrid>

            {modalGroup && (
                <CompatibleProductsModal
                    groups={groups}
                    activeType={modalGroup.type}
                    onSelectType={setModalType}
                    onClose={() => setModalType(undefined)}
                />
            )}
        </>
    );
};

const CompatibleTypeTeaser = ({
    group,
    active,
    showArrow = true,
    onOpen,
}: {
    group: CompatibleGroup;
    active?: boolean;
    showArrow?: boolean;
    onOpen: () => void;
}) => {
    const previewItems = useMemo(() => {
        const ordered = [...group.items.filter(hasImage), ...group.items.filter((item) => !hasImage(item))];

        const picked: Component[] = [];
        const seenOems = new Set<string>();

        for (const item of ordered) {
            if (picked.length >= PREVIEW_COUNT) break;

            const oem = CollectionHelpers.id(item.manufacturer);
            if (!oem || seenOems.has(oem)) continue;

            seenOems.add(oem);
            picked.push(item);
        }

        for (const item of ordered) {
            if (picked.length >= PREVIEW_COUNT) break;
            if (!picked.includes(item)) picked.push(item);
        }

        return picked;
    }, [group]);

    return (
        <UnstyledButton className={cx.teaser} data-active={active || undefined} onClick={onOpen}>
            <Box className={cx.teaserThumbs}>
                {previewItems.map((item, index) => (
                    <Box
                        key={item.id}
                        className={cx.teaserThumb}
                        style={{ marginLeft: index === 0 ? 0 : -8, zIndex: PREVIEW_COUNT - index }}
                    >
                        <ComponentThumbnail
                            component={item}
                            showEmpty
                            style={{ width: 30, height: 30, mixBlendMode: 'multiply' }}
                        />
                    </Box>
                ))}
            </Box>

            <Stack gap={0} style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} lh={1.2} truncate>
                    {group.label}
                </Text>
                <Text fz="sm" c="dimmed">
                    {group.items.length} {group.items.length === 1 ? 'product' : 'products'}
                </Text>
            </Stack>

            {showArrow && <IoArrowForward className={cx.teaserArrow} size={18} />}
        </UnstyledButton>
    );
};

const CompatibleProductsModal = ({
    groups,
    activeType,
    onSelectType,
    onClose,
}: {
    groups: CompatibleGroup[];
    activeType: ComponentType;
    onSelectType: (type: ComponentType) => void;
    onClose: () => void;
}) => {
    const isMobile = useIsMobile();

    const activeGroup = groups.find((group) => group.type === activeType) ?? groups[0];

    return (
        <Modal
            fullScreen={isMobile}
            opened
            withinPortal
            onClose={onClose}
            size="min(1000px, calc(100vw - 2rem))"
            title={
                <Stack gap={2}>
                    <Text fw={700} fz="lg">
                        {activeGroup.label}
                    </Text>
                    <Text fz="sm" c="dimmed">
                        {activeGroup.items.length} compatible {activeGroup.items.length === 1 ? 'product' : 'products'}
                    </Text>
                </Stack>
            }
        >
            <DatasheetMantineProviderReset>
                <Stack gap="md">
                    {groups.length > 1 && (
                        <Flex gap="sm" wrap="wrap">
                            {groups.map((group) => (
                                <CompatibleTypeTeaser
                                    key={group.type}
                                    group={group}
                                    active={group.type === activeGroup.type}
                                    showArrow={false}
                                    onOpen={() => onSelectType(group.type)}
                                />
                            ))}
                        </Flex>
                    )}

                    <CompatibleProductsBrowser key={activeGroup.type} group={activeGroup} />
                </Stack>
            </DatasheetMantineProviderReset>
        </Modal>
    );
};

const CompatibleProductsBrowser = ({ group }: { group: CompatibleGroup }) => {
    const [search, setSearch] = useState('');
    const [debouncedSearch] = useDebouncedValue(search, 200);
    const [manufacturer, setManufacturer] = useState<string | null>(null);

    const manufacturerIds = useMemo(
        () => [
            ...new Set(
                group.items.map((item) => CollectionHelpers.id(item.manufacturer)).filter((id): id is string => !!id),
            ),
        ],
        [group],
    );

    const { companies } = useCompanyProfiles({ ids: manufacturerIds, execute: manufacturerIds.length > 0 });

    const manufacturerNameById = useMemo(() => {
        const map = new Map<string, string>();
        companies.forEach((company) => map.set(company.id, company.name));
        return map;
    }, [companies]);

    // Precompute a lowercase search haystack (name, part number, manufacturer) per product.
    const indexed = useMemo(
        () =>
            group.items.map((component) => {
                const { name, identifier } = ComponentHelpers.generateMetadata(component);
                const manufacturerId = CollectionHelpers.id(component.manufacturer);
                const manufacturerName = manufacturerId ? manufacturerNameById.get(manufacturerId) : undefined;

                return {
                    component,
                    manufacturerId,
                    haystack: [name, identifier, manufacturerName].filter(Boolean).join(' ').toLowerCase(),
                };
            }),
        [group, manufacturerNameById],
    );

    const manufacturerOptions = useMemo(
        () =>
            [...manufacturerNameById.entries()]
                .map(([value, label]) => ({ value, label }))
                .sort((a, b) => a.label.localeCompare(b.label)),
        [manufacturerNameById],
    );

    const query = debouncedSearch.trim().toLowerCase();

    const filtered = useMemo(
        () =>
            indexed
                .filter((item) => (query ? item.haystack.includes(query) : true))
                .filter((item) => (manufacturer ? !!item.manufacturerId && manufacturer === item.manufacturerId : true))
                .map((item) => item.component),
        [indexed, query, manufacturer],
    );

    // Memoized so typing doesn't re-render the heavy product cards.
    const grid = useMemo(
        () =>
            filtered.length ? (
                <GridSection nbCols={3}>
                    {filtered.map((component) => (
                        <ComponentOverviewHit key={component.id} component={component} />
                    ))}
                </GridSection>
            ) : (
                <Text c="dimmed" ta="center" py="xl">
                    No products match your search.
                </Text>
            ),
        [filtered],
    );

    const isFiltered = !!query || !!manufacturer;

    const clearFilters = () => {
        setSearch('');
        setManufacturer(null);
    };

    return (
        <>
            <Group wrap="wrap" gap="sm" align="flex-start">
                <TextInput
                    size="md"
                    flex={1}
                    miw={200}
                    leftSection={<IoSearch />}
                    placeholder="Search name, part number or manufacturer"
                    value={search}
                    onChange={(event) => setSearch(event.currentTarget.value)}
                />
                <Select
                    size="md"
                    miw={200}
                    data={manufacturerOptions}
                    value={manufacturer}
                    onChange={(value) => setManufacturer(value || null)}
                    placeholder="Choose manufacturer"
                />
            </Group>

            <Group justify="space-between" gap="xs">
                <Text fz="sm" c="dimmed">
                    Showing {filtered.length} of {group.items.length}
                </Text>
                {isFiltered && (
                    <Button variant="subtle" size="compact-sm" onClick={clearFilters}>
                        Clear filters
                    </Button>
                )}
            </Group>

            {grid}
        </>
    );
};

const CompatibleWith = () => {
    const [compatibleWith, compatibleWithPlaceholders] = useWatch({
        name: ['compatibleWith', 'compatibleWithPlaceholders'],
    }) as [Component['compatibleWith'], Component['compatibleWithPlaceholders']];

    const mode = useDatasheetMode();

    const { components: compatibleComponents } = useProductsByIds(compatibleWith ?? []);

    if (mode !== DatasheetMode.VIEW) {
        return (
            <ComponentDatasheetSection
                id={ComponentSection.COMPATIBLE_WITH}
                title="Compatible with"
                afterTitle={<ComponentDatasheetSectionProgress section={ComponentSection.COMPATIBLE_WITH} />}
                alwaysShow
            >
                <CompatibleWithField />
            </ComponentDatasheetSection>
        );
    }

    if (!compatibleComponents.length && !compatibleWithPlaceholders.length) {
        return null;
    }

    return (
        <ComponentDatasheetSection
            alwaysShow
            title="Compatible with"
            id={ComponentSection.COMPATIBLE_WITH}
            afterTitle={<ComponentDatasheetSectionProgress section={ComponentSection.COMPATIBLE_WITH} />}
        >
            {!!compatibleComponents.length && <CompatibleProducts components={compatibleComponents} />}

            {!!compatibleWithPlaceholders.length && (
                <Stack gap="xs">
                    {!!compatibleComponents.length && (
                        <Title order={3} size="h2" mt="md">
                            Also compatible with
                        </Title>
                    )}

                    <GridSection nbCols={4}>
                        {compatibleWithPlaceholders.map(({ manufacturer, productIdentifier, componentType }) => (
                            <Box className={cx.compatibleWith} key={`${manufacturer}-${productIdentifier}`}>
                                <CompatiblePlaceholder
                                    manufacturerName={manufacturer}
                                    productIdentifier={productIdentifier}
                                    componentType={componentType}
                                />
                            </Box>
                        ))}
                    </GridSection>
                </Stack>
            )}
        </ComponentDatasheetSection>
    );
};

export { CompatibleWith };
